import { useEffect, useState } from 'react'
import { Container, Row, Col, Card, Table, Button, Modal, Form, Alert, InputGroup, Pagination } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getTasks, addTask } from '../../../actions/taskActions'
import { getGroups } from '../../../actions/classActions'
import { getStudents } from '../../../actions/studentActions'
import Loader from '../../../components/loaders/Loader'
import { toastSuccess, toastError } from '../../../components/swal/toast'

export default function TaskList() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { tasks, loading, taskError } = useSelector(state => state.tasks)
    const { groups } = useSelector(state => state.classes)
    const { students } = useSelector(state => state.students)
    const [showModal, setShowModal] = useState(false)
    const [form, setForm] = useState({ title: '', description: '', dueDate: '', groupId: '', assignType: 'group', studentId: '', studentName: '' })
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const perPage = 9

    useEffect(() => {
        dispatch(getTasks())
        dispatch(getGroups())
    }, [dispatch])

    useEffect(() => {
        setPage(1)
    }, [search])

    useEffect(() => {
        if (form.groupId && form.assignType === 'individual') {
            dispatch(getStudents(form.groupId))
        }
    }, [form.groupId, form.assignType, dispatch])

    if (loading && (!tasks || tasks.length === 0)) return <Loader />

    if (taskError) return (
        <Container className='my-3 p-5'>
            <Alert variant='danger'>{taskError}</Alert>
        </Container>
    )

    const handleSubmit = () => {
        if (!form.title.trim()) {
            toastError(t('teacher.tasks.titleRequired'))
            return
        }
        if (form.assignType === 'individual') {
            if (!form.studentId) {
                toastError(t('teacher.tasks.selectStudent'))
                return
            }
            dispatch(addTask({
                title: form.title,
                description: form.description,
                dueDate: form.dueDate,
                studentId: form.studentId,
                studentName: form.studentName,
                groupName: ''
            }))
        } else {
            if (!form.groupId) {
                toastError(t('teacher.tasks.selectGroup'))
                return
            }
            const selectedGroup = groups.find(g => String(g.odooGroupId || g._id) === String(form.groupId))
            dispatch(addTask({ ...form, groupName: selectedGroup?.name || '' }))
        }
        setShowModal(false)
        setForm({ title: '', description: '', dueDate: '', groupId: '', assignType: 'group', studentId: '', studentName: '' })
        toastSuccess(t('teacher.tasks.taskAssigned'))
    }

    const handleGroupChange = (e) => {
        setForm({ ...form, groupId: e.target.value, studentId: '', studentName: '' })
    }

    const handleStudentChange = (e) => {
        const student = students.find(s => String(s.id) === String(e.target.value))
        setForm({ ...form, studentId: e.target.value, studentName: student?.name || '' })
    }

    const filtered = (tasks || []).filter(task => {
        if (!search) return true
        const q = search.toLowerCase()
        return (task.title || '').toLowerCase().includes(q) ||
               (task.groupName || '').toLowerCase().includes(q) ||
               (task.studentName || '').toLowerCase().includes(q) ||
               (task.description || '').toLowerCase().includes(q)
    })

    const totalPages = Math.ceil(filtered.length / perPage)
    const paginated = filtered.slice((page - 1) * perPage, page * perPage)

    return (
        <Container className='my-3 p-5'>
            <Row>
                <Col className='d-flex justify-content-between align-items-center'>
                    <div>
                        <h3 className='fw-bold mb-1'>{t('teacher.tasks.title')}</h3>
                        <p className='text-muted mb-0'>{tasks?.length || 0} {t('teacher.tasks.taskcount')}</p>
                    </div>
                    <Button variant='primary' className='rounded-3' onClick={() => setShowModal(true)}>
                        {t('teacher.tasks.newtask')}
                    </Button>
                </Col>
            </Row>

            <Row className='mt-3 mb-3'>
                <Col md={6} lg={4}>
                    <InputGroup>
                        <InputGroup.Text className='bg-white border-end-0'>
                            <i className='fa-solid fa-magnifying-glass' style={{ color: '#9ca3af', fontSize: '14px' }}></i>
                        </InputGroup.Text>
                        <Form.Control
                            placeholder={t('teacher.tasks.searchPlaceholder')}
                            className='border-start-0'
                            style={{ borderRadius: '0 10px 10px 0' }}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </InputGroup>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Card className='border-0 shadow-sm rounded-4'>
                        <Card.Body className='p-0'>
                            {paginated.length > 0
                                ? <Table hover responsive className='mb-0'>
                                    <thead className='bg-light'>
                                        <tr>
                                            <th className='fw-semibold' style={{ fontSize: '13px' }}>{t('teacher.tasks.tasktitle')}</th>
                                            <th className='fw-semibold' style={{ fontSize: '13px' }}>{t('teacher.tasks.group')}</th>
                                            <th className='fw-semibold' style={{ fontSize: '13px' }}>{t('teacher.tasks.student')}</th>
                                            <th className='fw-semibold' style={{ fontSize: '13px' }}>{t('teacher.tasks.duedate')}</th>
                                            <th className='fw-semibold' style={{ fontSize: '13px' }}>{t('teacher.tasks.status')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginated.map((task, idx) => {
                                            const st = task.grade !== null && task.grade !== undefined
                                                ? 'graded'
                                                : task.dueDate && new Date(task.dueDate) < new Date() ? 'overdue' : 'pending'
                                            const variant = st === 'graded' ? 'success' : st === 'overdue' ? 'danger' : 'warning'
                                            const label = st === 'graded' ? t('teacher.tasks.graded') : st === 'overdue' ? t('teacher.tasks.overdue') : t('teacher.tasks.pending')
                                            return (
                                                <tr key={task._id || idx}>
                                                    <td className='fw-semibold'>
                                                        <Link to={`/teacher/tasks/${task._id}`} className='text-decoration-none'>{task.title}</Link>
                                                    </td>
                                                    <td><span className='badge bg-light text-dark border'>{task.groupName || '-'}</span></td>
                                                    <td className='text-muted'>{task.studentName || '-'}</td>
                                                    <td className='text-muted'>{task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '-'}</td>
                                                    <td><span className={`badge bg-${variant} rounded-pill`}>{label}{st === 'graded' ? ` (${task.grade})` : ''}</span></td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </Table>
                                : <div className='p-4 text-center'>
                                    <p className='text-muted mb-0'>
                                        {search ? t('global.results') : t('teacher.tasks.notasks')}
                                    </p>
                                </div>
                            }
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {totalPages > 1 && (
                <Row className='mt-3'>
                    <Col className='d-flex justify-content-center'>
                        <Pagination>
                            <Pagination.First onClick={() => setPage(1)} disabled={page === 1} />
                            <Pagination.Prev onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} />
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                                .reduce((acc, p, idx, arr) => {
                                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...')
                                    acc.push(p)
                                    return acc
                                }, [])
                                .map((p, idx) =>
                                    p === '...'
                                        ? <Pagination.Ellipsis key={`ellipsis-${idx}`} disabled />
                                        : <Pagination.Item key={p} active={p === page} onClick={() => setPage(p)}>{p}</Pagination.Item>
                                )}
                            <Pagination.Next onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} />
                            <Pagination.Last onClick={() => setPage(totalPages)} disabled={page === totalPages} />
                        </Pagination>
                    </Col>
                </Row>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} size='lg'>
                <Modal.Header closeButton>
                    <Modal.Title>{t('teacher.tasks.newtask')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className='mb-3'>
                            <Form.Label className='fw-semibold'>{t('teacher.tasks.assignto')} *</Form.Label>
                            <div className='d-flex gap-3'>
                                <Form.Check
                                    type='radio'
                                    id='assignGroup'
                                    label={t('teacher.tasks.grouptotal')}
                                    checked={form.assignType === 'group'}
                                    onChange={() => setForm({ ...form, assignType: 'group', studentId: '', studentName: '' })}
                                />
                                <Form.Check
                                    type='radio'
                                    id='assignIndividual'
                                    label={t('teacher.tasks.individualstudent')}
                                    checked={form.assignType === 'individual'}
                                    onChange={() => setForm({ ...form, assignType: 'individual', studentId: '', studentName: '' })}
                                />
                            </div>
                        </Form.Group>

                        <Form.Group className='mb-3'>
                            <Form.Label>{t('teacher.tasks.group')} *</Form.Label>
                            <Form.Select style={{ borderRadius: '10px' }}
                                value={form.groupId} onChange={handleGroupChange}>
                                <option value=''>{t('teacher.tasks.selectgroup')}</option>
                                {groups.map(g => (
                                    <option key={g.odooGroupId || g._id} value={g.odooGroupId || g._id}>{g.name} - {g.courseName}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {form.assignType === 'individual' && form.groupId && (
                            <Form.Group className='mb-3'>
                                <Form.Label>{t('teacher.tasks.student')} *</Form.Label>
                                <Form.Select style={{ borderRadius: '10px' }}
                                    value={form.studentId} onChange={handleStudentChange}>
                                    <option value=''>{t('teacher.tasks.selectstudent')}</option>
                                    {students.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        )}

                        <Form.Group className='mb-3'>
                            <Form.Label>{t('teacher.tasks.tasktitle')} *</Form.Label>
                            <Form.Control type='text' style={{ borderRadius: '10px' }}
                                value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
                        </Form.Group>
                        <Form.Group className='mb-3'>
                            <Form.Label>{t('teacher.tasks.description')}</Form.Label>
                            <Form.Control as='textarea' rows={3} style={{ borderRadius: '10px' }}
                                value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
                        </Form.Group>
                        <Form.Group className='mb-3'>
                            <Form.Label>{t('teacher.tasks.duedate')}</Form.Label>
                            <Form.Control type='date' style={{ borderRadius: '10px' }}
                                value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={() => setShowModal(false)}>{t('global.cancel')}</Button>
                    <Button variant='primary' onClick={handleSubmit}>{t('teacher.tasks.assign')}</Button>
                </Modal.Footer>
            </Modal>
        </Container>
    )
}
